/**
 * Page-editing mode — the gate for page-scoped capture (mirror of
 * states-editing-mode.ts for the Pages strip).
 *
 * While ON and a page context is active, edits to SHARED children of a Pages
 * container route into that page's state on the child (lazily keyed on first
 * edit; trace-viewer's `pageCaptureContext` / `capturePageState`). While OFF,
 * edits change the base scene on every page.
 *
 * Session-only, defaults OFF: the mode is driven by the Pages TAB in the
 * shared strip (strip-tabs.tsx) — open tab = capture on, closed = base
 * editing — so which context edits route to is always visible. The "All"
 * card additionally provides base editing while the tab is open.
 */

import type { SceneObject } from 'trace-viewer/scene';
import type { StateDefinition } from 'trace-viewer/behaviors';
import { capturePageState, isSharedIntent, liveLocalPose, pageCaptureContext, TransformCommand, type PageEditing, type TransformSnapshot } from 'trace-viewer/editor';
import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { pushCommand } from './behaviors/pagesSharedActions';

let enabled = false;

const listeners = new Set<(enabled: boolean) => void>();

export function setPageEditingEnabled(next: boolean): void {
    if (enabled === next) return;
    enabled = next;
    for (const l of listeners) l(next);
}

/**
 * Whether an edit to a container's shared content goes to the showing page:
 * the mode is on and the container's context is a page, not its "Shared"
 * card. The `pageEditing` port of trace-viewer's page capture and ops.
 */
export const pageEditing: PageEditing = container => enabled && !isSharedIntent(container);

/**
 * Write `mutate` into the showing page's state on `obj`, lazily keying it, and
 * push the one undo entry once written. False, writing nothing, when the edit
 * is Base's (no page context). A capture that faults has taken back what it
 * wrote; `failed` then undoes what the gesture already showed.
 */
export function captureForPage(
    obj: SceneObject,
    label: (pageName: string) => string,
    mutate: (state: StateDefinition) => StateDefinition,
    failed?: () => void,
): boolean {
    const page = pageCaptureContext(obj, pageEditing);
    if (!page) return false;
    const name = label(page.pageName);
    void capturePageState(page, name, mutate).then(pushCommand, (error: unknown) => {
        console.error(`[page capture] "${name}" failed:`, error);
        failed?.();
    });
    return true;
}


/**
 * Route a finished drag or typed edit on `obj` into the showing page's state,
 * as its whole live local pose less a position a layout writes. False,
 * writing nothing, when it is Base's. If the capture faults and the node is
 * still where the edit left it, it goes back to `before`, where the edit
 * started.
 */
export function captureTransformForPage(obj: SceneObject, before?: TransformSnapshot): boolean {
    if (!pageCaptureContext(obj, pageEditing)) return false;
    // Read now, where the edit ended: the capture writes it after an await.
    const { position, ...rest } = liveLocalPose(obj);
    const pose = obj.getRuntimeOwnedTransformFields().position ? rest : { position, ...rest };
    const ended = TransformCommand.capture(obj);
    const putBack = () => {
        const now = TransformCommand.capture(obj);
        if (before && JSON.stringify(now) === JSON.stringify(ended)) showPose(obj, before);
    };
    return captureForPage(obj, page => `Key ${obj.name} on ${page}`, state => ({ ...state, transform: pose }), putBack);
}

/** Put `obj` back at `pose` on screen only, in its rotation mode; nothing recorded it. */
function showPose(obj: SceneObject, pose: TransformSnapshot): void {
    const node = obj.transform;
    node.position = Vector3.FromArray(pose.position);
    const rotation = Quaternion.FromArray(pose.rotation);
    if (node.rotationQuaternion) node.rotationQuaternion = rotation;
    else node.rotation = rotation.toEulerAngles();
    node.scaling = Vector3.FromArray(pose.scale);
}

/** Subscribe to mode changes; returns an unsubscribe. */
export function onPageEditingChanged(listener: (enabled: boolean) => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}
